import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./Kaizen1.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function Kaizen1(props) {
  const { x1200PxLogo_Icam__20081 } = props;
  const [hasImplemented, setHasImplemented] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    // Part 1: Current State - Kaizen Application Today
    // Kaizen Culture & Organization
    dedicatedStructure: "",
    participationPercentage: "",
    improvementIdeasCount: "",
    suggestionsAnalysis: "",
    problemTypes: "",
    
    // Kaizen Actions Implementation
    ideasFormalization: "",
    kaizenMethodology: "",
    implementationDelay: "",
    measurableGains: "",
    regularReviews: "",
    
    // Part 2: Vision and Desired Future State
    // Kaizen Ambitions
    volumeObjectives: "",
    teamsIntegration: "",
    teamAutonomy: "",
    supportTools: "",
    cultureReinforcement: "",
    
    // Part 3: Monitoring, Gap Analysis & Continuous Improvement Governance
    // Governance & Indicators
    performanceIndicators: "",
    dedicatedDashboard: "",
    biggestSuccesses: "",
    obstacles: "",
    failureManagement: "",
    
    // Training & Engagement
    employeeTraining: "",
    managerTraining: "",
    bestPracticesValorization: "",
    kaizenRituals: "",
    roadmap: ""
  });
  
  const [uploadStatus, setUploadStatus] = useState({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
    fileName: ""
  });
  const fileInputRef = useRef(null);
  const questionnaireRef = useRef(null);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File type validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/svg+xml', 
                         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                         'application/vnd.ms-excel',
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        ...uploadStatus,
        error: "Unsupported file type. Please upload PDF, JPG, PNG, SVG, Excel or Word formats.",
        success: false
      });
      return;
    }

    // File size validation (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setUploadStatus({
        ...uploadStatus,
        error: "File too large. Maximum allowed size: 10MB.",
        success: false
      });
      return;
    }

    try {
      setUploadStatus({
        isUploading: true,
        progress: 0,
        error: null,
        success: false,
        fileName: file.name
      });

      // Prepare FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('answers', JSON.stringify(answers));
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadStatus(prev => ({
            ...prev,
            progress
          }));
        }
      };

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.response));
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
      });

      xhr.open('POST', '/api/kaizen/upload');
      xhr.send(formData);

      const response = await uploadPromise;

      setUploadStatus({
        isUploading: false,
        progress: 100,
        error: null,
        success: true,
        fileName: file.name,
        fileId: response.file_id
      });

    } catch (error) {
      setUploadStatus({
        isUploading: false,
        progress: 0,
        error: "An error occurred during upload. Please try again.",
        success: false,
        fileName: ""
      });
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers({
      ...answers,
      [field]: value
    });
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    } else {
      setShowImport(true);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    }
  };

  const handleYesClick = () => {
    setHasImplemented(true);
    setShowQuestionnaire(true);
  };

  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
      </div>
    );
  };

  const renderQuestionnaire = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 1: Current State - Kaizen Application Today</h2>
            
            <div className="question-group">
              <h3 className="question-group-title">Kaizen Culture & Organization</h3>
              
              <div className="question-item">
                <label htmlFor="dedicatedStructure">Do you have a dedicated structure or Kaizen manager?</label>
                <textarea 
                  id="dedicatedStructure" 
                  value={answers.dedicatedStructure} 
                  onChange={(e) => handleAnswerChange('dedicatedStructure', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="participationPercentage">What percentage of employees actively contribute to Kaizen initiatives?</label>
                <textarea 
                  id="participationPercentage" 
                  value={answers.participationPercentage} 
                  onChange={(e) => handleAnswerChange('participationPercentage', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="improvementIdeasCount">How many continuous improvement ideas are proposed each month or quarter?</label>
                <textarea 
                  id="improvementIdeasCount" 
                  value={answers.improvementIdeasCount} 
                  onChange={(e) => handleAnswerChange('improvementIdeasCount', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="suggestionsAnalysis">Are employees' suggestions always analyzed, prioritized, and tracked?</label>
                <textarea 
                  id="suggestionsAnalysis" 
                  value={answers.suggestionsAnalysis} 
                  onChange={(e) => handleAnswerChange('suggestionsAnalysis', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="problemTypes">What types of problems are most often addressed? (Safety, quality, delays, wasteâ€¦)</label>
                <textarea 
                  id="problemTypes" 
                  value={answers.problemTypes} 
                  onChange={(e) => handleAnswerChange('problemTypes', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Kaizen Actions Implementation</h3>
              
              <div className="question-item">
                <label htmlFor="ideasFormalization">How are Kaizen ideas formalized? (paper form, digital toolâ€¦)</label>
                <textarea 
                  id="ideasFormalization" 
                  value={answers.ideasFormalization} 
                  onChange={(e) => handleAnswerChange('ideasFormalization', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="kaizenMethodology">Do you have a structured Kaizen methodology? (PDCA, A3, DMAICâ€¦)</label>
                <textarea 
                  id="kaizenMethodology" 
                  value={answers.kaizenMethodology} 
                  onChange={(e) => handleAnswerChange('kaizenMethodology', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="implementationDelay">What is the average delay between the idea proposal and its implementation?</label>
                <textarea 
                  id="implementationDelay" 
                  value={answers.implementationDelay} 
                  onChange={(e) => handleAnswerChange('implementationDelay', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="measurableGains">Are Kaizen actions oriented toward measurable gains? (time, quality, costâ€¦)</label>
                <textarea 
                  id="measurableGains" 
                  value={answers.measurableGains} 
                  onChange={(e) => handleAnswerChange('measurableGains', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="regularReviews">Do you conduct regular reviews or presentations of completed Kaizen actions?</label>
                <textarea 
                  id="regularReviews" 
                  value={answers.regularReviews} 
                  onChange={(e) => handleAnswerChange('regularReviews', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title"> Part 2: Vision & Desired Future State</h2>
            
            <div className="question-group">
              <h3 className="question-group-title"> Kaizen Ambitions</h3>
              
              <div className="question-item">
                <label htmlFor="volumeObjectives">What objectives do you have regarding Kaizen volume or impact for the coming year?</label>
                <textarea 
                  id="volumeObjectives" 
                  value={answers.volumeObjectives} 
                  onChange={(e) => handleAnswerChange('volumeObjectives', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="teamsIntegration">Do you want to engage more teams in Kaizen? (support functions, management, procurementâ€¦)</label>
                <textarea 
                  id="teamsIntegration" 
                  value={answers.teamsIntegration} 
                  onChange={(e) => handleAnswerChange('teamsIntegration', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="teamAutonomy">Are you aiming for full team autonomy in identifying and solving problems?</label>
                <textarea 
                  id="teamAutonomy" 
                  value={answers.teamAutonomy} 
                  onChange={(e) => handleAnswerChange('teamAutonomy', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="supportTools">What tools or systems do you want to deploy to support Kaizen (visual boards, digital ideas systemâ€¦)?</label>
                <textarea 
                  id="supportTools" 
                  value={answers.supportTools} 
                  onChange={(e) => handleAnswerChange('supportTools', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="cultureReinforcement">Do you want to strengthen the Kaizen culture through challenges or rewards?</label>
                <textarea 
                  id="cultureReinforcement" 
                  value={answers.cultureReinforcement} 
                  onChange={(e) => handleAnswerChange('cultureReinforcement', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 3: Monitoring, Gap Analysis & Continuous Improvement Governance</h2>
            
            <div className="question-group">
              <h3 className="question-group-title">Governance & Indicators</h3>
              
              <div className="question-item">
                <label htmlFor="performanceIndicators">Which performance indicators do you use to evaluate Kaizen efficiency? (number of ideas, implementation rate, ROIâ€¦)</label>
                <textarea 
                  id="performanceIndicators" 
                  value={answers.performanceIndicators} 
                  onChange={(e) => handleAnswerChange('performanceIndicators', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="dedicatedDashboard">Do you have a dedicated continuous improvement dashboard?</label>
                <textarea 
                  id="dedicatedDashboard" 
                  value={answers.dedicatedDashboard} 
                  onChange={(e) => handleAnswerChange('dedicatedDashboard', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="biggestSuccesses">What are the greatest successes obtained thanks to Kaizen?</label>
                <textarea 
                  id="biggestSuccesses" 
                  value={answers.biggestSuccesses} 
                  onChange={(e) => handleAnswerChange('biggestSuccesses', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="obstacles">What obstacles or resistance have you faced when implementing Kaizen?</label>
                <textarea 
                  id="obstacles" 
                  value={answers.obstacles} 
                  onChange={(e) => handleAnswerChange('obstacles', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="failureManagement">How do you manage failures or ideas that do not lead to an action?</label>
                <textarea 
                  id="failureManagement" 
                  value={answers.failureManagement} 
                  onChange={(e) => handleAnswerChange('failureManagement', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title"> Training & Engagement</h3>
              
              <div className="question-item">
                <label htmlFor="employeeTraining">Have your employees been trained in Kaizen principles and tools?</label>
                <textarea 
                  id="employeeTraining" 
                  value={answers.employeeTraining} 
                  onChange={(e) => handleAnswerChange('employeeTraining', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="managerTraining">Are frontline managers trained and involved in leading Kaizen initiatives?</label>
                <textarea 
                  id="managerTraining" 
                  value={answers.managerTraining} 
                  onChange={(e) => handleAnswerChange('managerTraining', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="bestPracticesValorization">How do you highlight Kaizen best practices and success stories?</label>
                <textarea 
                  id="bestPracticesValorization" 
                  value={answers.bestPracticesValorization} 
                  onChange={(e) => handleAnswerChange('bestPracticesValorization', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="kaizenRituals">Do you have daily, weekly, or monthly Kaizen rituals? (meetings, briefings, Gemba walkâ€¦)</label>
                <textarea 
                  id="kaizenRituals" 
                  value={answers.kaizenRituals} 
                  onChange={(e) => handleAnswerChange('kaizenRituals', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="roadmap">What is your roadmap for making Kaizen a strategic competitiveness lever?</label>
                <textarea 
                  id="roadmap" 
                  value={answers.roadmap} 
                  onChange={(e) => handleAnswerChange('roadmap', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleDownloadPDF = async () => {
  try {
    const element = document.querySelector(".kaizen1-page");
    if (!element) {
      alert("âŒ Element not found!");
      return;
    }

    document.querySelectorAll("*").forEach((el) => {
      const bgImage = window.getComputedStyle(el).backgroundImage;
      if (bgImage && bgImage.includes("gradient")) {
        el.style.backgroundImage = "none";
      }

      const bgColor = window.getComputedStyle(el).backgroundColor;
      if (bgColor.includes("var(") || bgColor === "transparent" || bgColor === "rgba(0, 0, 0, 0)") {
        el.style.backgroundColor = "#ffffff";
      }
    });

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    const date = new Date().toISOString().slice(0, 10);
    pdf.save(`Kaizen_${date}.pdf`);
    alert("âœ… PDF successfully generated!");
  } catch (err) {
    console.error("PDF Error:", err);
    alert("âŒ PDF Error: " + (err.message || "unknown"));
  }
};

  const saveLeanAnswers = async (moduleName) => {
      try {
        const caseId = localStorage.getItem("caseId");
        if (!caseId) {
          alert("No active case found!");
          console.warn("LocalStorage is empty. Current content:", localStorage);
          return;
        }

        console.log(`ðŸ’¾ Saving ${moduleName} answers for case ${caseId}`);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lean/${moduleName}/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            case_id: caseId,
            answers: answers,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || `Saving error (${response.status}): ${data.message}`);
        }

        alert(`âœ… ${moduleName.toUpperCase()} answers saved successfully!`);

        handleNextStep();

      } catch (error) {
        console.error("âŒ saveLeanAnswers error:", error);
        alert("âŒ " + error.message);
      }
};

  return (
    <div className="container-center-horizontal">
      <div className="kaizen1-page screen">
        <div className="header">
          <Link to="/home">
            <img
              className="logo-icam"
              src="/img/1200px-logo-icam---2008-1@2x.png"
              alt="ICAM Logo"
            />
          </Link>
        </div>
        
        <div className="questionnaire-container">
          <h1 className="question-title">Kaizen Methodology - Continuous Improvement</h1>
          
          {!showQuestionnaire ? (
            <div className="question-section">
              <p className="question-text">
                Has your company already implemented the Kaizen methodology?
              </p>
              
              <div className="buttons-container">
                <button 
                  className={`choice-button ${hasImplemented ? 'active' : ''}`}
                  onClick={handleYesClick}
                >
                  Yes
                </button>
                <button 
                  className={`choice-button ${!hasImplemented ? 'active' : ''}`}
                  onClick={() => setHasImplemented(false)}
                >
                  No
                </button>
              </div>

              {!hasImplemented && (
                <div className="redirect-section">
                  <p className="redirect-text">
                    We recommend starting with the basic Lean principles:
                  </p>
                  <Link to="/smed-1" className="redirect-button">
                    Go to SMED
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <>
              {!showImport ? (
                <div className="questionnaire-container-inner" ref={questionnaireRef}>
                  {renderProgressIndicator()}
                  {renderQuestionnaire()}
                  
                  <div className="navigation-buttons">
                    {currentStep > 1 && (
                      <button 
                        className="nav-button prev-button"
                        onClick={handlePrevStep}
                      >
                        Previous
                      </button>
                    )}
                    
                    <button 
                      className="nav-button next-button"
                      onClick={() => saveLeanAnswers("kaizen")}
                    >
                      {currentStep < 3 ? "Next" : "Finish"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="file-upload-section">
                  <h2 className="upload-title">Import your existing documents</h2>
                  <p className="upload-description">
                    You can now import your Kaizen documents, dashboards, or improvement sheets:
                  </p>
                  
                  <div className="upload-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden-file-input"
                      accept=".pdf,.jpg,.jpeg,.png,.svg,.xlsx,.xls,.doc,.docx"
                      onChange={handleFileUpload}
                      disabled={uploadStatus.isUploading}
                    />
                    
                    <button 
                      className="import-button"
                      onClick={handleImportClick}
                      disabled={uploadStatus.isUploading}
                    >
                      {uploadStatus.isUploading ? 'Uploading...' : 'Import file'}
                    </button>
                    
                    {uploadStatus.isUploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${uploadStatus.progress}%` }}
                          ></div>
                        </div>
                        <p className="progress-text">Uploading... {uploadStatus.progress}%</p>
                      </div>
                    )}

                    {uploadStatus.error && (
                      <div className="upload-error">
                        <p>{uploadStatus.error}</p>
                      </div>
                    )}

                    {uploadStatus.success && (
                      <div className="upload-success">
                        <p>The file {uploadStatus.fileName} was uploaded successfully!</p>
                        {uploadStatus.fileId && (
                          <button 
                            className="download-button"
                            onClick={() => window.open(`/api/kaizen/${uploadStatus.fileId}`, '_blank')}
                          >
                            View file
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="navigation-buttons">
                    <button 
                      className="nav-button prev-button"
                      onClick={() => setShowImport(false)}
                    >
                      Back to questionnaire
                    </button>
                    
                    <Link to="/kaizen1-1" className="nav-button next-button">
                      Continue
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
          <button 
            className="download-button"
            onClick={handleDownloadPDF}
            disabled={uploadStatus.isUploading}
          >
            {uploadStatus.isUploading ? "Generating PDF..." : "Download PDF"}
          </button>
      </div>
    </div>
  );
}

export default Kaizen1;
